import { type ReactElement, useMemo, useState } from 'react'
import {
  getProgramAutoEnrollmentTriggers,
  isEmpty,
  toProgramTrigger,
  type ProgramAutoEnrollmentTrigger,
  type ProgramId,
  type ProgramTrigger,
} from '@terros/common'
import { MultiSelect, Stack } from '@mantine/core'
import { type SelectItem, useWorkflowForCanvassFeature } from '@front/model'
import { type WithProgramForm } from './type'
import { ProgramTriggerWarnings } from './ProgramTriggerWarnings'
import { ProgramTriggerValues } from './ProgramTriggerValues'
import {
  getProgramTriggerTypes,
  getTriggersOfType,
  isProgramTriggerType,
  toAutoEnrollmentTriggers,
  type ProgramTriggerType,
} from './programTriggers'

const BASE_TRIGGER_OPTIONS: SelectItem<ProgramTriggerType>[] = [
  { value: 'userCreated', label: 'Terros User Start Date' },
  { value: 'role', label: 'Role Assignment' },
  { value: 'team', label: 'Team Assignment' },
  { value: 'program', label: 'Completion of previous program' },
]
const ACCOUNT_STAGE_OPTION: SelectItem<ProgramTriggerType> = { value: 'status', label: 'Account Stage' }
const WORKFLOW_TRIGGER_OPTIONS: SelectItem<ProgramTriggerType>[] = [
  { value: 'workflowStage', label: 'Workflow Stage' },
  { value: 'workflowAction', label: 'Workflow Action' },
]

type Props = WithProgramForm & {
  programId?: ProgramId
}

export const ProgramTriggerSelect = ({ form, programId }: Props): ReactElement => {
  const triggers = getProgramAutoEnrollmentTriggers(form.values)
  const [triggerTypes, setTriggerTypes] = useState<ProgramTriggerType[]>(getProgramTriggerTypes(triggers))
  const triggerOptions = useProgramTriggerOptions(triggerTypes)

  const writeTriggers = (updated: ProgramAutoEnrollmentTrigger[]): void => {
    const [first] = updated
    form.setFieldValue('triggers', updated)
    form.setFieldValue('trigger', first ? toProgramTrigger(first) : null)
  }

  const handleTypesChange = (values: string[]): void => {
    const types = values.filter(isProgramTriggerType)
    setTriggerTypes(types)
    writeTriggers(types.flatMap((type) => getTriggersOfType(triggers, type)))
  }

  const setTypeValues = (type: ProgramTriggerType, values: ProgramTrigger[]): void => {
    writeTriggers(
      triggerTypes.flatMap((current) =>
        current === type ? toAutoEnrollmentTriggers(values) : getTriggersOfType(triggers, current)
      )
    )
  }

  return (
    <Stack gap='xs'>
      <MultiSelect
        clearable
        data={triggerOptions}
        label='Program Auto Enrollment'
        placeholder={isEmpty(triggerTypes) ? 'Select auto enrollment triggers' : undefined}
        value={triggerTypes}
        onChange={handleTypesChange}
      />
      {triggerTypes.map((type) => (
        <ProgramTriggerValues
          key={type}
          triggerType={type}
          triggers={getTriggersOfType(triggers, type)}
          setTriggerValues={(values) => setTypeValues(type, values)}
          programId={programId}
        />
      ))}
      <ProgramTriggerWarnings triggers={triggers} scopeTeamIds={form.values.teamIds} />
    </Stack>
  )
}

const useProgramTriggerOptions = (triggerTypes: ProgramTriggerType[]): SelectItem<ProgramTriggerType>[] => {
  const isWorkflowEnabled = useWorkflowForCanvassFeature().isEnabled

  return useMemo(() => {
    const stageOptions = isWorkflowEnabled ? WORKFLOW_TRIGGER_OPTIONS : [ACCOUNT_STAGE_OPTION]
    const options = [...BASE_TRIGGER_OPTIONS, ...stageOptions]
    const inUse = [ACCOUNT_STAGE_OPTION, ...WORKFLOW_TRIGGER_OPTIONS].filter(
      (option) => triggerTypes.includes(option.value) && !options.some((o) => o.value === option.value)
    )
    return [...options, ...inUse]
  }, [isWorkflowEnabled, triggerTypes])
}
